import { RefObject, useRef, useContext, Dispatch, SetStateAction } from "react";
import {
  BundlePlatformEnum,
  useDeleteModelBundlePlatformMutation,
  ModelBundleFileTypeEnum,
} from "../../../generated/graphql";
import { BundleNames } from "../../MobileAppModal/MobileAppModelModal/constants";
import {
  CurrentBundleContainer,
  MobileAppModalBundleName,
} from "../../MobileAppModal/MobileAppModal.style";
import {
  BundleLink,
  ButtonCustom,
  ImportFileUpload,
} from "../../shared/Style/Style";
import { Collapse, IconButton } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import { BundleFormModalContext } from "../modals/BundleFormModalContext";
import { initialNotifications } from "../../Gamification/Awards/constants";

type BundleDataType = {
  name: BundleNames;
  bundleLink: string;
  type: ModelBundleFileTypeEnum;
  inputRef: RefObject<HTMLInputElement>;
};

const PLATFORM_TO_BUNDLE_MAP: { [key in BundlePlatformEnum]?: string[] } = {
  [BundlePlatformEnum.Ios]: [BundleNames.Ios, BundleNames.IosManifest],
  [BundlePlatformEnum.Android]: [
    BundleNames.Android,
    BundleNames.AndroidManifest,
  ],
  [BundlePlatformEnum.WebGl]: [BundleNames.WebGL, BundleNames.WebGlManifest],
  [BundlePlatformEnum.Vr]: [BundleNames.Vr, BundleNames.VrManifest],
  [BundlePlatformEnum.InteractiveWhiteboardAndroid]: [
    BundleNames.AndroidBoardBundle,
    BundleNames.AndroidBoardManifest,
  ],
  [BundlePlatformEnum.InteractiveWhiteboardWindows]: [
    BundleNames.WindowsBoardBundle,
    BundleNames.WindowsBoardManifest,
  ],
};

interface AppModelBundlesProps {
  setNotifications: Dispatch<
    SetStateAction<{
      error: boolean;
      success: boolean;
      message: string;
    }>
  >;
}

export const AppModelBundles = ({ setNotifications }: AppModelBundlesProps) => {
  const iosInput = useRef<HTMLInputElement | null>(null);
  const webGLInput = useRef<HTMLInputElement | null>(null);
  const androidInput = useRef<HTMLInputElement | null>(null);
  const vrExperimentInput = useRef<HTMLInputElement | null>(null);
  const androidBoardInput = useRef<HTMLInputElement | null>(null);
  const windowsBoardInput = useRef<HTMLInputElement | null>(null);
  const manifestInputIos = useRef<HTMLInputElement | null>(null);
  const manifestInputWebGL = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const manifestInputVr = useRef<HTMLInputElement | null>(null);
  const manifestInputAndroidBoard = useRef<HTMLInputElement | null>(null);
  const manifestInputWindowsBoard = useRef<HTMLInputElement | null>(null);

  const { modelData, activePlatform, updateModelData } = useContext(
    BundleFormModalContext,
  );

  const [deleteModelBundlePlatform, { loading }] =
    useDeleteModelBundlePlatformMutation();

  const contentType = modelData.contentType;
  const platforms = modelData.platforms || [];

  const addFileHandler = (
    field: ModelBundleFileTypeEnum,
    file: File,
    platform: BundlePlatformEnum,
  ) => {
    const currentPlatformData = platforms.find((p) => p.platform === platform);
    updateModelData("platforms", [
      // @ts-ignore
      ...platforms.filter((p) => p.platform !== platform),
      // @ts-ignore
      {
        ...(currentPlatformData ? { ...currentPlatformData } : { platform }),
        [field.toLowerCase()]: file,
      },
    ]);
  };

  const deleteFileHandler = async (fileTypes: ModelBundleFileTypeEnum) => {
    if (!activePlatform) return;
    const currentPlatform = platforms.find(
      (p) => p.platform === activePlatform,
    );
    if (
      modelData?.id &&
      !(currentPlatform?.bundle || currentPlatform?.manifest)
    ) {
      await deleteModelBundlePlatform({
        variables: {
          deleteModelBundlePlatformId: modelData.id,
          platform: activePlatform,
          fileTypes: [fileTypes],
        },
        onCompleted(data) {
          if (data?.deleteModelBundlePlatform) {
            updateModelData(data.deleteModelBundlePlatform);
          }
        },
        onError() {
          setNotifications({
            ...initialNotifications,
            error: true,
            message: "Сталась помилка при видаленні файлу, спробуйте пізніше",
          });
        },
      });
      return;
    }
    updateModelData(
      "platforms",
      platforms.map((i) =>
        i.platform === activePlatform
          ? { ...i, [fileTypes.toLowerCase()]: null }
          : i,
      ),
    );
  };

  const bundlesData = [
    {
      name: BundleNames.Ios,
      bundleLink: modelData.iosBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: iosInput,
    },
    {
      name: BundleNames.WebGL,
      bundleLink: modelData.webGlBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: webGLInput,
    },
    {
      name: BundleNames.Android,
      bundleLink: modelData.androidBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: androidInput,
    },
    {
      name: BundleNames.Vr,
      bundleLink: modelData.vrBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: vrExperimentInput,
    },
    {
      name: BundleNames.AndroidBoardBundle,
      bundleLink: modelData.interactiveWhiteboardAndroidBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: androidBoardInput,
    },
    {
      name: BundleNames.WindowsBoardBundle,
      bundleLink: modelData.interactiveWhiteboardWindowsBundleUrl,
      type: ModelBundleFileTypeEnum.Bundle,
      inputRef: windowsBoardInput,
    },
    {
      name: BundleNames.IosManifest,
      bundleLink: modelData.iosManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputIos,
    },
    {
      name: BundleNames.WebGlManifest,
      bundleLink: modelData.webGlManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputWebGL,
    },
    {
      name: BundleNames.AndroidManifest,
      bundleLink: modelData.androidManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputAndroid,
    },
    {
      name: BundleNames.VrManifest,
      bundleLink: modelData.vrManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputVr,
    },
    {
      name: BundleNames.AndroidBoardManifest,
      bundleLink: modelData.interactiveWhiteboardAndroidManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputAndroidBoard,
    },
    {
      name: BundleNames.WindowsBoardManifest,
      bundleLink: modelData.interactiveWhiteboardWindowsManifestUrl,
      type: ModelBundleFileTypeEnum.Manifest,
      inputRef: manifestInputWindowsBoard,
    },
  ];

  const visibleBundles = PLATFORM_TO_BUNDLE_MAP[activePlatform] || [];

  const filteredBundlesData = bundlesData.filter((bundle) =>
    visibleBundles.includes(bundle.name),
  );

  const renderCurrentBundle = ({
    name,
    bundleLink,
    type,
    inputRef,
  }: BundleDataType) => {
    const platformData = platforms.find((p) => p.platform === activePlatform);
    const currentFile =
      type === ModelBundleFileTypeEnum.Bundle
        ? platformData?.bundle
        : platformData?.manifest;

    return (
      <CurrentBundleContainer key={name}>
        <MobileAppModalBundleName>{name}</MobileAppModalBundleName>

        {!!bundleLink || !!currentFile ? (
          <span>
            <BundleLink href={bundleLink}>{name}</BundleLink>

            <IconButton onClick={() => deleteFileHandler(type)}>
              <Delete color="secondary" />
            </IconButton>
          </span>
        ) : (
          <ButtonCustom
            disabled={loading}
            onClick={() => inputRef.current?.click()}
          >
            <span>Прикріпити</span>
            <ImportFileUpload
              type="file"
              ref={inputRef}
              onChange={({ target: { files } }) =>
                files?.[0] &&
                activePlatform &&
                addFileHandler(type, files[0], activePlatform)
              }
            />
          </ButtonCustom>
        )}
      </CurrentBundleContainer>
    );
  };

  return (
    <>
      <Collapse in={!!contentType} collapsedSize="0px">
        {filteredBundlesData.map((data) =>
          renderCurrentBundle({
            ...data,
          }),
        )}
      </Collapse>
    </>
  );
};
